import React, { useCallback, useMemo, useState } from "react";
import type { Project } from "../../../../../../../../../api/useProjects";
import ModalForm from "@ui/ModalV2/Form";
import t from "../../../../../../../../../i18n";
import Text from "@ui/Text";
import { DatePicker } from "@ui/DatePicker";
import { parseAbsolute, toCalendarDate, type DateValue } from "@internationalized/date";
import formatDate from "@ui/i18n/formatDate";
import { css } from "@linaria/core";
import type { ManualFaultEvent } from "../../../../../../../../../api/useManualFaultEvents";
import { colors, spacings } from "@ui/Theme";
import type { Quantity } from "../../../../../../../../../api/useQuantities";
import { usePost } from "../../../../../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import SignedNumberInput, { getSignedNumberInputError } from "../../SignedNumberInput";

interface AddPeriodModalFormProps {
  project: Project;
  manualFaultEvent: ManualFaultEvent;
  quantities: Quantity[];
  closeModal: () => void;
}

const AddPeriodModalForm = ({
  project,
  manualFaultEvent,
  quantities,
  closeModal,
}: AddPeriodModalFormProps) => {
  const { addToast } = useToasts();
  const createTimeWindow = usePost(
    "/projects/:projectId(uuid)/manualFaultEvents/:manualFaultEventId(uuid)",
  );

  /** Section Date */
  const [startsAt, setStartsAt] = useState<DateValue | null>(null);
  const dateBoundaries = React.useMemo(() => {
    const startDate = new Date(manualFaultEvent.startsAt.getTime());
    startDate.setHours(0, 0, 0, 0);
    startDate.setDate(startDate.getDate() + 1);

    const endDate = new Date(manualFaultEvent.metadata.endsAt.getTime());
    endDate.setHours(23, 59, 59, 999);

    return {
      startDate,
      endDate,
      startDateValue: parseAbsolute(startDate.toISOString(), project.timezone),
      endDateValue: parseAbsolute(endDate.toISOString(), project.timezone),
    };
  }, [manualFaultEvent, project.timezone]);
  const onStartsAtDatePickerChange = React.useCallback(
    (value: DateValue | null) => {
      const resValue = value;
      if (resValue === null) {
        setStartsAt(null);
      } else {
        resValue.set({
          hour: 0,
          minute: 0,
          second: 0,
        });
        const date = toCalendarDate(resValue).toDate(project.timezone);
        setStartsAt(parseAbsolute(date.toISOString(), project.timezone));
      }
    },
    [project.timezone],
  );
  const errorStartedDate = React.useMemo(() => {
    if (startsAt === null) return;

    if (
      project.analysesAggregate?.latestActiveAnalysis &&
      startsAt >
        parseAbsolute(project.analysesAggregate.latestActiveAnalysis.date, project.timezone)
    ) {
      return t(
        "project.events.manual_fault.window.startsAt.error.after_latest_analysis",
        formatDate(
          new Date(project.analysesAggregate.latestActiveAnalysis.date),
          "slash",
          project.timezone,
        ),
      );
    }

    const lastStartsAt = manualFaultEvent.windows.at(-1)?.startsAt;
    if (lastStartsAt && startsAt <= parseAbsolute(lastStartsAt.toISOString(), project.timezone)) {
      return t(
        "project.events.manual_fault.window.startsAt.error.previous_existing_period",
        formatDate(lastStartsAt, "slash", project.timezone),
      );
    }

    if (startsAt < dateBoundaries.startDateValue) {
      const errorDate = new Date(dateBoundaries.startDate);
      errorDate.setDate(errorDate.getDate() - 1);
      return t(
        "project.events.manual_fault.window.startsAt.error.before_event_start",
        formatDate(errorDate, "slash", project.timezone),
      );
    }

    return;
  }, [
    startsAt,
    dateBoundaries.startDateValue,
    dateBoundaries.startDate,
    project.timezone,
    manualFaultEvent.windows,
    project.analysesAggregate,
  ]);

  /**Section Impact */
  const performanceIndicators = useMemo(
    () =>
      quantities
        .filter(q => q.qoiOrder !== null)
        .sort((a, b) => (a.qoiOrder ?? 0) - (b.qoiOrder ?? 0)),
    [quantities],
  );
  const impactedPerformanceIndicator = useMemo(
    () =>
      performanceIndicators.reduce(
        (acc, impact) => {
          acc[impact.id] = "0";
          return acc;
        },
        {} as Record<string, string>,
      ),
    [performanceIndicators],
  );
  const [impactInputs, setImpactInputs] = useState(impactedPerformanceIndicator);
  const [errorInputs, setErrorInputs] = useState(
    performanceIndicators.reduce(
      (acc, pi) => {
        acc[pi.id] = null;
        return acc;
      },
      {} as Record<string, string | null>,
    ),
  );
  const handleErrorInput = useCallback((value: string): string | null => {
    if (getSignedNumberInputError(value))
      return t("project.events.manualFaultEvent.modifyModalForm.errors.should_be_digital");
    return null;
  }, []);

  const handleSubmit = useCallback(async () => {
    if (startsAt === null) return false;

    const impacts = performanceIndicators.map(pi => {
      const val = impactInputs[pi.id];
      return {
        quantityId: pi.id,
        value: val === undefined || val === "" ? 0 : parseFloat(val),
      };
    });

    try {
      await createTimeWindow({
        routeParams: {
          projectId: project.id,
          manualFaultEventId: manualFaultEvent.id,
        },
        body: {
          startsAt: startsAt.toDate(project.timezone),
          impacts,
        },
      });
      addToast("success", t("project.events.manualFaultEvent.modal_form.add_period.toast.success"));
      closeModal();
      return true;
    } catch (error) {
      addToast("error", t("errors.backend_error"));
      return false;
    }
  }, [
    startsAt,
    performanceIndicators,
    impactInputs,
    createTimeWindow,
    project.id,
    manualFaultEvent.id,
    closeModal,
    project.timezone,
    addToast,
  ]);

  const hasErrors = useMemo(
    () =>
      Object.values(errorInputs).some(error => error !== null) || errorStartedDate !== undefined,
    [errorInputs, errorStartedDate],
  );

  return (
    <ModalForm
      cancellable
      onRequestClose={closeModal}
      onSubmit={startsAt === null || hasErrors ? false : handleSubmit}
      okButtonName={t("project.events.modalForm.okButton.add")}
    >
      <Text>{t("project.events.modalForm.addPeriod.description")}</Text>

      <DatePicker
        className={datePickerWidth}
        id=""
        label={t(
          "project.events.manual_fault.impact_time_windows_change.datepicker.startsAt.label",
        )}
        dateOptions={{
          shouldCloseOnSelect: true,
          hideTimeZone: true,
          granularity: "day",
          defaultValue: dateBoundaries.startDateValue,
          minValue: dateBoundaries.startDateValue,
          maxValue: dateBoundaries.endDateValue,
          value: startsAt,
          isDateUnavailable: date => {
            const normalize = (d: DateValue) =>
              d.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

            const normDate = normalize(date);
            const min = normalize(dateBoundaries.startDateValue);
            const max = dateBoundaries.endDateValue;

            if (normDate.compare(min) < 0) return true;
            if (normDate.compare(max) > 0) return true;
            if (
              manualFaultEvent.windows.some(window => {
                const winDate = normalize(
                  parseAbsolute(window.startsAt.toISOString(), project.timezone),
                );
                return normDate.compare(winDate) <= 0;
              })
            )
              return true;
            return false;
          },
          onChange: onStartsAtDatePickerChange,
        }}
        error={errorStartedDate}
        disabled={
          formatDate(manualFaultEvent.startsAt, "slash", project.timezone) ===
          formatDate(manualFaultEvent.metadata.endsAt, "slash", project.timezone)
        }
        required
      />

      <div className={impactPIs}>
        <Text type="body1SemiBold" className={title}>
          {t("project.events.manualFaultEvent.modifyModalForm.impactedPIs")}
        </Text>
        {performanceIndicators.map(pi => {
          const key = `${pi.id}`;
          return (
            <SignedNumberInput
              className={input}
              key={key}
              label={`${pi.name} ${pi.unit && `(${pi.unit})`}`}
              placeholder={
                errorInputs[pi.id]
                  ? t("project.events.manualFaultEvent.modifyModalForm.input.placeholder")
                  : undefined
              }
              value={impactInputs[pi.id] ?? ""}
              defaultNegative
              onValueChange={value => {
                setImpactInputs(prev => {
                  const existingImpact = prev[pi.id];
                  if (existingImpact === undefined) return prev;

                  return {
                    ...prev,
                    [pi.id]: value,
                  };
                });
                setErrorInputs(prev => ({
                  ...prev,
                  [pi.id]: handleErrorInput(value),
                }));
              }}
              error={errorInputs[pi.id] || undefined}
            />
          );
        })}
      </div>
    </ModalForm>
  );
};

export default AddPeriodModalForm;

//#region CSS
const datePickerWidth = css`
  width: 360px;
`;
const title = css`
  color: ${colors.carbon800};
`;
const impactPIs = css`
  display: flex;
  flex-direction: column;
  margin-top: ${spacings.s};
  row-gap: ${spacings.m};
`;
const input = css`
  div:has(> input) {
    width: 110px;
  }
`;
//#endregion CSS
